import { NgTemplateOutlet } from '@angular/common'
import { Component, computed, contentChild, Directive, input, TemplateRef } from '@angular/core'
import type { DayPeriod } from '@seiri/shared/common'

/**
 * Marks the template one item of a `seiri-day-group` is rendered with. Bound to the day's periods
 * only so the template's `let-item` is typed: `<ng-template [seiriDayItem]="day.periods" let-slot>`.
 */
@Directive({ selector: 'ng-template[seiriDayItem]' })
export class DayItemDirective<T> {
  readonly seiriDayItem = input.required<readonly DayPeriod<T>[]>()

  static ngTemplateContextGuard<T>(_directive: DayItemDirective<T>, _context: unknown): _context is { $implicit: T } {
    return true
  }
}

/**
 * One day of a list of days: its heading with the count, and its items. A day with items both
 * before and from noon labels the two groups "Dopoludnia" and "Popoludní". Bare: no surface of its
 * own, so the booking flow's free times and the public timetable's termíny look the same.
 */
@Component({
  selector: 'seiri-day-group',
  imports: [NgTemplateOutlet],
  template: `
    <h3 class="day__title" [id]="headingId()">
      {{ label() }} <span class="day__count">{{ count() }}</span>
    </h3>
    @for (period of periods(); track period.key) {
      @let periodId = headingId() + '-' + period.key;
      @if (split()) {
        <h4 class="day__period" [id]="periodId">{{ period.label }}</h4>
      }
      <ul
        class="day__items"
        [class.day__items--stack]="layout() === 'stack'"
        [attr.aria-labelledby]="split() ? headingId() + ' ' + periodId : headingId()"
      >
        @for (item of period.items; track trackBy()(item)) {
          <li><ng-container *ngTemplateOutlet="itemTemplate(); context: { $implicit: item }" /></li>
        }
      </ul>
    }
  `,
  styles: `
    :host {
      display: block;
      margin-top: var(--space-20);
    }
    .day__title {
      margin: 0 0 var(--space-8);
      font: var(--mat-sys-title-small);
      color: var(--theme-fg-heading);
    }
    .day__count {
      margin-inline-start: var(--space-4);
      font: var(--mat-sys-body-small);
      color: var(--theme-fg-muted);
    }
    .day__period {
      margin: var(--space-12) 0 var(--space-6);
      font: var(--mat-sys-label-medium);
      color: var(--theme-fg-muted);
    }
    .day__title + .day__period {
      margin-top: 0;
    }
    .day__items {
      display: flex;
      flex-wrap: wrap;
      gap: var(--space-8);
      margin: 0;
      padding: 0;
      list-style: none;
    }
    .day__items--stack {
      flex-direction: column;
      flex-wrap: nowrap;
    }
  `,
})
export class DayGroupComponent<T> {
  /** The day as `yyyy-MM-dd`; names the heading's id. */
  readonly key = input.required<string>()
  /** "Pondelok 5. októbra". */
  readonly label = input.required<string>()
  /** "4 voľné termíny", "3 termíny". */
  readonly count = input.required<string>()
  readonly periods = input.required<readonly DayPeriod<T>[]>()
  readonly trackBy = input.required<(item: T) => string>()
  /** Starts the heading ids; two lists on one page (the public page keeps both modules alive) need their own. */
  readonly idPrefix = input('day')
  /** `wrap`: items side by side, as the booking flow's time chips. `stack`: one full-width item a row. */
  readonly layout = input<'wrap' | 'stack'>('wrap')

  protected readonly itemTemplate = contentChild.required(DayItemDirective, { read: TemplateRef })
  protected readonly headingId = computed(() => `${this.idPrefix()}-${this.key()}`)
  protected readonly split = computed(() => this.periods().length > 1)
}
